import { Flex, SearchInput, Spacing } from '@billy/design-system'
import { Table } from '@shinetools/lumen-react'
import { ChangeEvent, ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FecAccountMappingFilter } from '../../../../enums/fecAccountMappingFilter'
import { FecAccountMappingSortKey } from '../../../../enums/fecAccountMappingSortKey'
import { FecAccountMappingStatus } from '../../../../enums/fecAccountMappingStatus'
import type { FecAccountMapping } from '../../../../types/fecAccountMapping'
import { filterFecAccountMappings } from '../../../../utils/filterFecAccountMappings'
import {
  getFecAccountMappingSortDirection,
  sortFecAccountMappings,
  toggleFecAccountMappingSort,
} from '../../../../utils/sortFecAccountMappings'
import { FecImportMappingTableScrollArea } from '../FecImportMappingTableScrollArea'
import { ChartOfAccountsFilterToggle } from './elements/ChartOfAccountsFilterToggle'
import { ChartOfAccountsTableBody } from './elements/ChartOfAccountsTableBody'
import { ChartOfAccountsTableHeader } from './elements/ChartOfAccountsTableHeader'
import * as Styled from './styles'

interface FecImportChartOfAccountsTableProps {
  accountMappings: FecAccountMapping[]
  isLoading: boolean
}

export const FecImportChartOfAccountsTable = ({
  accountMappings,
  isLoading,
}: FecImportChartOfAccountsTableProps): ReactElement => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [filter, setFilter] = useState<FecAccountMappingFilter>(FecAccountMappingFilter.All)
  const [sort, setSort] = useState({ isDescending: false, key: FecAccountMappingSortKey.AccountNo })

  const newCount = useMemo(
    () => accountMappings.filter((mapping) => mapping.status === FecAccountMappingStatus.New).length,
    [accountMappings],
  )

  const visibleMappings = useMemo(
    () =>
      sortFecAccountMappings({
        accountMappings: filterFecAccountMappings({ accountMappings, filter, searchQuery }),
        isDescending: sort.isDescending,
        sortKey: sort.key,
      }),
    [accountMappings, filter, searchQuery, sort],
  )

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }, [])

  const handleSearchClear = useCallback(() => {
    setSearchQuery('')
  }, [])

  const handleSort = useCallback((key: FecAccountMappingSortKey) => {
    setSort((currentSort) => toggleFecAccountMappingSort(currentSort, key))
  }, [])

  return (
    <Styled.ChartOfAccountsTableWrapper>
      <Flex alignItems="center" gap={Spacing.S12} width="100%">
        <SearchInput
          fullWidth
          onChange={handleSearchChange}
          onClear={handleSearchClear}
          placeholder={t(
            'settings.organization.accounting.fec_file_import.modal.mapping_step.chart_of_accounts.search_placeholder',
          )}
          value={searchQuery}
        />
        <ChartOfAccountsFilterToggle newCount={newCount} onChange={setFilter} value={filter} />
      </Flex>

      <FecImportMappingTableScrollArea>
        <Table compact>
          <Table.Content>
            <ChartOfAccountsTableHeader
              accountNoSortDirection={getFecAccountMappingSortDirection(sort, FecAccountMappingSortKey.AccountNo)}
              onSort={handleSort}
              statusSortDirection={getFecAccountMappingSortDirection(sort, FecAccountMappingSortKey.Status)}
            />
            <ChartOfAccountsTableBody isLoading={isLoading} mappings={visibleMappings} />
          </Table.Content>
        </Table>
      </FecImportMappingTableScrollArea>
    </Styled.ChartOfAccountsTableWrapper>
  )
}
